import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { NbDialogService } from '@nebular/theme';
import { ToastrService } from 'ngx-toastr';
import Swal from 'sweetalert2';
import { GlobalCategoryService } from 'src/app/services/global-category.service';
import { GlobalCategoryFormDialogComponent } from '../global-category-form-dialog/global-category-form-dialog.component';

@Component({
  selector: 'app-global-categories-tab',
  templateUrl: './global-categories-tab.component.html',
  styleUrls: ['./global-categories-tab.component.scss']
})
export class GlobalCategoriesTabComponent implements OnInit {

  @Input() isAdmin = false;
  @Output() categoriesChanged = new EventEmitter<void>();

  categories: any[] = [];
  loading = false;

  constructor(
    private globalCategoryService: GlobalCategoryService,
    private dialogService: NbDialogService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.loadCategories();
  }

  loadCategories(): void {
    this.loading = true;
    this.globalCategoryService.listCategories().subscribe({
      next: (res: any) => {
        const data = this.globalCategoryService.extractListData(res);
        this.categories = data.categories;
        this.loading = false;
      },
      error: (err: any) => {
        this.loading = false;
        this.toastr.error(err?.error?.response?.message || 'Failed to load global categories');
      }
    });
  }

  openAddRoot(): void {
    this.openFormDialog(null, null);
  }

  openAddChild(parent: any): void {
    this.openFormDialog(null, parent._id);
  }

  openEdit(cat: any): void {
    this.openFormDialog(cat, null);
  }

  private openFormDialog(category: any | null, parentCategoryId: string | null): void {
    this.dialogService.open(GlobalCategoryFormDialogComponent, {
      context: {
        category,
        parentCategoryId,
        allCategories: this.categories
      },
      hasBackdrop: true,
      closeOnBackdropClick: false
    }).onClose.subscribe((saved: boolean) => {
      if (saved) {
        this.loadCategories();
        this.categoriesChanged.emit();
      }
    });
  }

  deleteCategory(cat: any): void {
    Swal.fire({
      title: 'Delete category?',
      text: `"${cat.name}" will be soft-deleted if it has no children or products.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#dc3545'
    }).then((result) => {
      if (!result.isConfirmed) {
        return;
      }
      this.globalCategoryService.deleteCategory(cat._id).subscribe({
        next: () => {
          this.toastr.success('Category deleted');
          this.loadCategories();
          this.categoriesChanged.emit();
        },
        error: (err: any) => {
          this.toastr.error(err?.error?.response?.message || err?.error?.message || 'Delete failed');
        }
      });
    });
  }

  canMoveUp(cat: any): boolean {
    const siblings = this.getSiblings(cat);
    const idx = siblings.findIndex((s) => String(s._id) === String(cat._id));
    return idx > 0;
  }

  canMoveDown(cat: any): boolean {
    const siblings = this.getSiblings(cat);
    const idx = siblings.findIndex((s) => String(s._id) === String(cat._id));
    return idx >= 0 && idx < siblings.length - 1;
  }

  moveUp(cat: any): void {
    this.swapSibling(cat, -1);
  }

  moveDown(cat: any): void {
    this.swapSibling(cat, 1);
  }

  private parentKey(cat: any): string {
    const p = cat.parentCategory;
    if (p == null) {
      return '__root__';
    }
    return String(typeof p === 'object' ? p._id : p);
  }

  private getSiblings(cat: any): any[] {
    const key = this.parentKey(cat);
    return this.categories.filter((c) => this.parentKey(c) === key);
  }

  private swapSibling(cat: any, direction: -1 | 1): void {
    const siblings = [...this.getSiblings(cat)];
    const idx = siblings.findIndex((s) => String(s._id) === String(cat._id));
    const swapIdx = idx + direction;
    if (idx < 0 || swapIdx < 0 || swapIdx >= siblings.length) {
      return;
    }
    const tmp = siblings[idx];
    siblings[idx] = siblings[swapIdx];
    siblings[swapIdx] = tmp;
    const items = siblings.map((s, i) => ({ id: s._id, displayOrder: i }));
    this.globalCategoryService.reorderCategories(items).subscribe({
      next: (res: any) => {
        const data = this.globalCategoryService.extractListData(res);
        this.categories = data.categories;
        this.toastr.success('Order updated');
        this.categoriesChanged.emit();
      },
      error: (err: any) => {
        this.toastr.error(err?.error?.response?.message || 'Reorder failed');
      }
    });
  }
}
